import type { Envelope, PayloadTag, EnvelopeDecodeOptions, EnvelopeEncodeOptions, EnvelopeSignOptions, SignaturePayload, PayloadSpec, DecodedEnvelope, DelegationPayload, InvocationPayload, Payload } from './types.js'
import type { ByteView } from 'multiformats'
import type { SignatureType } from 'iso-signatures/types'
import * as cbor from '@ipld/dag-cbor'
import { isObject } from './util.js'
import * as varsig from './index.js'

/** Version */
export const VERSION = '1.0.0-rc.1'

/**
 * Get the signature payload for a given payload
 */
export function getSignaturePayload (options:{
  spec:PayloadSpec
  payload:Payload
  version?:string
  signatureType:SignatureType
}):SignaturePayload {
    const { spec, version, signatureType, payload } = options

    const payloadTag:PayloadTag = `ucan/${spec}@${version ?? VERSION}` as PayloadTag

    const h = varsig.encode({ enc: 'DAG-CBOR', alg: signatureType })

    const signaturePayload = {
        h,
        [payloadTag]: payload,
    } as SignaturePayload

    return signaturePayload
}

/**
 * Decode a signature payload
 */
export function decodeSignaturePayload (bytes:Uint8Array) {
    const decoded = cbor.decode(bytes) as unknown

    if (!isObject(decoded)) {
        throw new TypeError(
      `Invalid signature payload expected object got ${typeof decoded}`
        )
    }
    if (!('h' in decoded)) {
        throw new TypeError('Invalid signature payload missing h')
    }
    const keys = Object.keys(decoded)
    if (keys.length !== 2) {
        throw new TypeError(
      `Invalid signature payload expected 2 keys got ${keys.length}`
        )
    }
    if (!keys[1].startsWith('ucan/dlg') && !keys[1].startsWith('ucan/inv')) {
        throw new TypeError('Invalid signature payload missing payload')
    }

    const payloadTag:PayloadTag = keys[1] as PayloadTag
    const { alg, enc } = varsig.decode((decoded as SignaturePayload).h)
    const payload = (decoded as SignaturePayload)[payloadTag]
    return {
        alg,
        enc,
        payload,
    }
}

/**
 * Check if a payload is an invocation payload
 */
export function isInvocationPayload (payload:Payload):payload is InvocationPayload {
    return 'args' in payload
}

/**
 * Check if a payload is a delegation payload
 */
export function isDelegationPayload (payload:Payload):payload is DelegationPayload {
    return 'pol' in payload
}

export async function sign (options:EnvelopeSignOptions) {
    const { spec, version, signer, payload } = options

    const signaturePayload = getSignaturePayload({
        spec,
        version,
        signatureType: signer.signatureType,
        payload,
    })
    const signature = await signer.sign(cbor.encode(signaturePayload))
    return {
        signature,
        signaturePayload,
    }
}

/**
 * Encode a UCAN envelope
 *
 * @see https://github.com/ucan-wg/spec#envelope
 */
export function encode (options:EnvelopeEncodeOptions):Uint8Array {
    const { signature, signaturePayload } = options

    const envelope = [signature, signaturePayload]

    return cbor.encode(envelope)
}

/**
 * Decode a UCAN envelope
 *
 * @see https://github.com/ucan-wg/spec#envelope
 */
export function decode<Spec extends PayloadSpec> (options:EnvelopeDecodeOptions):DecodedEnvelope<Spec> {
    const { envelope } = options

    const decoded = cbor.decode(envelope as ByteView<Envelope>) as Envelope

    const [signature, sigPayload] = decoded
    const [_h, payloadTag] = Object.keys(sigPayload)
    const header = sigPayload.h
    const payload:DelegationPayload | InvocationPayload = sigPayload[payloadTag as PayloadTag]

    const [_ucan, specVersion] = payloadTag.split('/')
    const [spec, version] = specVersion.split('@')

    const { alg, enc } = varsig.decode(header)

    return {
        alg,
        enc,
        signature,
        // Narrow payload type based on Spec
        payload,
        spec,
        version,
    } as DecodedEnvelope<Spec>
}
